import React, { useCallback, useEffect, useReducer } from 'react';

// assets
import { ICONS } from '../../../../../assets/icons';
import Landscape from '../../../../../assets/customSVG/Landscape';

// helpers
import { cn } from '../../../../../helper/cn';
import { replaceVariables } from '../../../../../helper/templates';
import {
  addLineBreake,
  replaceFormating,
  replaceVariablesForPreview,
} from '../../../../inbox/helper';

// utils
import {
  allowedCtaButtonTypes,
  allowedCustomButtonTypes,
  getCTAButtons,
} from '../../../../../components/commonComponents/templates/Buttons/utils';

// reducers
import {
  INITIAL_STATE,
  templateReducer,
} from '../../../../channels/reducers/templateReducer';

// components
import EdgeHandler from '../edge/Handler';
import CarouselCard from './CarouselCard';

import DocumentIcon from '../../../../../assets/customSVG/DocumentIcon';
import ReviewIcon from '../../../../../assets/customSVG/ReviewIcon';
import Tag from '../../../../../assets/customSVG/Tag';
import DocSvg from '../../../../../assets/customSVG/DocSvg';


const Template = ({
  category,
  categoryType,
  data,
  node,
  onEdgeMouseDown,
  checkIsEdgeConnected,
  allowToShowEdge = true,
  onButtonClick,
  btnIcon = true,
  rootClassName = '',
  className = '',
  btnClassName = '',
  rootBtnStyle = '',
  customBtnStyle,
  color,
  allowToEditFlow,
  previewType,
  previewMode,
}) => {
  const [templateData, templateDispatch] = useReducer(
    templateReducer,
    INITIAL_STATE
  );

  const { custom, authentication, carousel } = templateData;

  const {
    header,
    headerVariable,
    body,
    bodyVariables,
    footer,
    buttons,
    buttonsOrder,
    ctaButtons,
    customButtons,
  } = custom;

  useEffect(() => {
    templateDispatch({
      type: 'update_template',
      data: {
        category: category,
        subcategory_type: categoryType,
        components: data?.template,
      },
    });
  }, [data]);

  const showFooter = useCallback(() => {
    return getCTAButtons(buttons, 'OPT_OUT')?.length < 1 ? true : false;
  }, [buttons]);

  const btnStyle =
    'w-full h-[1.875rem] flex-1 text-[0.813rem] weight-medium text-[#007AFF] flex items-center justify-center gap-2.5';
  
  // Add Next Step draggable point
  const renderNextStepDraggable = () => {
    const nextStepPoint = node?.settings?.connections?.points?.find(
      (cp) => cp?.type === 'next_steps'
    );

    // For other node types, add the edge handler
    if (previewType === 'preview' || previewMode || node?.is_last_node) {
      return null;
    }

    return (
      <div className="flex items-center justify-end pt-[10px] pb-[2px] pr-2">
        <div className="flex items-center gap-1 bg-white">
          <span className="text-[12px] font-medium">Next Step</span>
          <div className="absolute right-[-4px]">
            <EdgeHandler
              type="start"
              start_node_id={node?.uniq_id}
              isConnected={checkIsEdgeConnected?.(node?.id, 'next')}
              onEdgeMouseDown={({ position, payload }) => {
                if (!checkIsEdgeConnected?.(node?.id, 'next')) {
                  onEdgeMouseDown?.({
                    connection_point: {
                      id: nextStepPoint?.id || node?.id,
                      type: 'next_steps',
                    },
                    position,
                    payload: {
                      ...payload,
                      nodeId: nextStepPoint?.id || node?.id,
                      type: 'next_steps',
                    },
                  });
                }
              }}
              color={color}
              allowToEditFlow={allowToEditFlow}
              draggable={true}
              draggableType="next_steps"
            />
          </div>
        </div>
      </div>
    );
  };


  const products = data?.payment_details?.products || []
  const orderDetails = data?.payment_details?.order_details || {}

  const renderOrderDetailTemplate = useCallback(() => {
    return (
      <div className="min-h-[4vw] bg-white rounded-[0.7vw] px-[0.3vw] py-[0.3vw] overflow-y-auto">
        {/* Order Header */}
        <div className="px-[0.5vw] py-[0.5vw] bg-[#0000000A] rounded-[0.4vw]">
          <div className="flex justify-between items-center">
            <span className="text-[0.5vw] text-[#606770]">ORDER # {'{{Order number}}'}</span>
          </div>
          <div className="border-t border-dashed border-[#0000001A] my-[0.3vw]"></div>
          <div className="flex flex-row items-center gap-3">
            {(() => {
              // First try to use product image if available
              const productWithImage = products?.find(p => p.image_url);
              if (productWithImage?.image_url) {
                return (
                  <img
                    src={productWithImage?.image_url}
                    alt="Product"
                    className="w-[45px] h-[45px] object-cover rounded-md"
                  />
                );
              }
              
              // Then try to use header image if available
              if (header?.format === "IMAGE" && header?.example?.header_handle?.[0]) {
                return (
                  <img
                    src={header?.example?.header_handle?.[0]}
                    alt=""
                    className="w-[45px] h-[45px] object-cover rounded-md"
                  />
                );
              }
              
              // Fallback to landscape icon
              return (
                <div className="flex items-center justify-center bg-[#A3A7AC] p-[0.5vw] rounded-md">
                  <div className="flex items-center justify-center">
                    <Landscape color="#FFFFFF" width={30} height={30} />
                  </div>
                </div>
              );
            })()}
            <div className="flex flex-col gap-1">
              <span className="text-[0.5vw]">
                {products?.map(p => p.name).filter(Boolean).join(', ') || `{{Product name}}`}
              </span>
              <span className="text-[0.5vw] text-[#606770]">
                Quantity {products?.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0) || '{{#}}'}
              </span>
            </div>
          </div>

          {(() => {
            // Calculate subtotal
            const subtotal = products?.reduce((sum, p) => {
              const price = parseFloat(p.price) || 0;
              const qty = parseInt(p.quantity, 10) || 0;
              const taxValue = parseFloat(orderDetails?.tax) || 0;

              // If tax is inclusive, calculate base price
              let itemPrice = price;
              if (orderDetails?.tax_type === 'tax_inclusive' && taxValue > 0) {
                itemPrice = price / (1 + taxValue / 100);
              }

              return sum + (itemPrice * qty);
            }, 0) || 0;

            // Calculate discount amount based on type
            const discountValue = parseFloat(orderDetails?.discount) || 0;
            const discountAmount = orderDetails?.discount_type === 'percent'
            ? (subtotal * discountValue) / 100
            : discountValue;

            // Calculate shipping
            const shipping = parseFloat(orderDetails?.shipping) || 0;

            // Calculate tax (applied after discount)
            const taxValue = parseFloat(orderDetails?.tax) || 0;
            let taxAmount = 0;

            if (orderDetails?.tax_type === 'tax_inclusive') {
              // Tax is already included in the prices
              // Formula: taxAmount = (totalWithTax * taxRate) / (100 + taxRate)
              const amountAfterDiscount = subtotal - discountAmount;
              taxAmount = amountAfterDiscount * (taxValue / 100);
            } else if (orderDetails?.tax_type === 'tax_exclusive') {
              // Tax needs to be added on top
              taxAmount = ((subtotal - discountAmount) * taxValue) / 100;
            } else {
              // If tax_type is not specified or is a fixed amount
              taxAmount = taxValue;
            }

            // Calculate total
            let total;
            if (orderDetails?.tax_type === 'tax_inclusive') {
              // For inclusive tax: total = base subtotal + tax + shipping - discount
              // The tax was extracted from original prices, so we add it back to get the original total
              total = subtotal - discountAmount + taxAmount + shipping;
            } else {
              // For exclusive tax: total = subtotal - discount + shipping + tax
              total = subtotal - discountAmount + shipping + taxAmount;
            }

            return (
              <>
                <div className="border-t border-dashed border-[#0000001A] my-[0.3vw]"></div>
                <div className="justify-between flex">
                  <span className="text-[0.5vw] text-[#606770]">Total</span>
                  <span className="text-[0.5vw] text-[#606770] font-medium">
                    ₹{total.toFixed(2)}
                  </span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Header */}
        {header?.format === 'TEXT' && (
          <div className='h-full text-base weight-semibold break-words'>
            {header?.example?.header_text?.[0] !== 0
              ? replaceVariables(
                  header?.text,
                  header?.example?.header_text
                )
              : header?.text}
            {/* {header?.text} */}
          </div>
        )}

        {/* Body */}
        <p
          className='h-auto text-[0.813rem] text-[#2D3036] weight-medium break-words bg-white mt-2'
          dangerouslySetInnerHTML={{
            __html:
              bodyVariables?.length > 0
                ? replaceVariablesForPreview(
                    addLineBreake(replaceFormating(body)),
                    bodyVariables
                  )
                : addLineBreake(replaceFormating(body)),
          }}></p>

        {/* Footer */}
        {footer?.text && (
          <p className='w-full text-[0.813rem] text-[var(--font-400)] weight-medium bg-white'>
            {footer.text}
          </p>
        )}

        {/* Order Detail Button */}
        <div className="h-auto px-[0.5vw] bg-white rounded-b-[0.7vw] mt-[0.8vw] text-xs">
          <p
            className="w-full text-[#007AFF] border bg-[#F8F9FC] text-center py-2 px-4 rounded-md"
          >
            Review and pay
          </p>
        </div>
      </div>
    );
  }, [header, footer]);

  return (
    <div className={cn('w-full min-w-72 h-full relative', rootClassName)}>
      <div
        className={cn(
          'w-auto h-auto overflow-y-scroll flex flex-col gap-2.5 p-3 bg-white rounded-xl overflow-hidden',
          className
        )}>
        <div className='w-full h-auto'>
          {category === 'AUTHENTICATION' ? (
            <div className='min-h-16 bg-white rounded-2.5 overflow-y-scroll'>
              <div className='text-sm text-[#2D3036] weight-medium break-words bg-white'>
                {authentication?.body}
              </div>
              <p className='w-full font-sm weight-small bg-white text-[var(--font-600)] pt-2'>
                {authentication?.content?.expiryTimeForCode
                  ? `This code expires in ${
                      authentication?.content?.expiryTime >= 1 &&
                      authentication?.content?.expiryTime <= 90
                        ? authentication?.content?.expiryTime
                        : 0
                    } Minutes.`
                  : ''}
              </p>
              <div className='h-auto bg-white rounded-b-lg mt-2.5 text-[#007AFF]'>
                {authentication?.buttons?.map((b, i) => {
                  return (
                    <div key={`authentication-preview-button-${i}`}>
                      <button className={`${btnStyle}`}>{b?.text}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : categoryType === 'ORDER_DETAIL' ?
              renderOrderDetailTemplate() :  (
            <div className='bg-white overflow-y-scroll flex flex-col gap-2'>
              {/* header preview */}
              <div className='w-full bg-white rounded-md h-full overflow-hidden'>
                {header?.format === 'TEXT' && (
                  <div className='h-full text-base weight-semibold break-words'>
                    {header?.example?.header_text?.[0] !== 0
                      ? replaceVariables(
                          header?.text,
                          header?.example?.header_text
                        )
                      : header?.text}
                    {/* {header?.text} */}
                  </div>
                )}
                {header?.format === 'IMAGE' &&
                  header?.example?.header_handle?.length > 0 && (
                    <img
                      src={header?.example?.header_handle?.[0]}
                      className='w-full h-full max-h-32 object-cover rounded'
                    />
                  )}
                {header?.format === 'VIDEO' &&
                  header?.example?.header_handle?.length > 0 && (
                    <video
                      className='object-cove rounded'
                      controls
                      autoplay='autoplay'
                      // loop
                    >
                      <source
                        src={header?.example?.header_handle?.[0]}
                        type='video/mp4'
                      />
                    </video>
                  )}
                {header?.format === 'DOCUMENT' &&
                  header?.example?.header_handle?.length > 0 && (
                    <iframe
                      title='cover'
                      className='imagePreview'
                      src={header?.example?.header_handle?.[0]}
                      type='application/pdf'
                      width='100%'
                      height='150px'></iframe>
                  )}
              </div>
              {/* body preview */}
              <p
                className='h-auto text-[0.813rem] text-[#2D3036] weight-medium break-words bg-white'
                dangerouslySetInnerHTML={{
                  __html:
                    bodyVariables?.length > 0
                      ? replaceVariablesForPreview(
                          addLineBreake(replaceFormating(body)),
                          bodyVariables
                        )
                      : addLineBreake(replaceFormating(body)),
                }}></p>
              {/* footer preview */}
              {footer?.text && showFooter() && (
                <p className='w-full text-[0.813rem] text-[var(--font-400)] weight-medium bg-white'>
                  {footer?.text}
                </p>
              )}
              {footer?.text && !showFooter() && (
                <p className='w-full text-[0.813rem] text-[var(--font-400)] weight-medium bg-white'>
                  Not interested? Tap Stop promotions
                </p>
              )}
              {/* buttons preview */}
              <div
                className={cn(
                  'h-auto bg-white rounded-b-2.5 text-[#007AFF] flex flex-col gap-2.5',
                  rootBtnStyle
                )}>
                {buttons.map((b, i) => {
                  const connection_point = {
                    id: b?.id,
                    type: 'action',
                  };
                  let isConnected = false;
                  if (checkIsEdgeConnected) {
                    isConnected = checkIsEdgeConnected({
                      start_node_id: node?.uniq_id,
                      connection_point,
                    });
                  }
                  return (
                    <div
                      className={cn(
                        'relative w-full h-[1.875rem] flex items-center justify-center bg-[#F8F9FC] rounded',
                        btnClassName
                      )}>
                      <div
                        className='w-full'
                        key={`preview-button-${i}`}
                        onClick={() => {
                          if (b?.type === 'QUICK_REPLY' && onButtonClick)
                            onButtonClick(b);
                        }}>
                        {allowedCtaButtonTypes.includes(b?.type) && (
                          <div className='w-full flex-1 opacity-50 cursor-not-allowed'>
                            {b.type === 'URL' && (
                              <div className={cn(btnStyle, customBtnStyle)}>
                                {btnIcon && (
                                  <img
                                    src={ICONS?.nounLink}
                                    alt='shareBtn'
                                    className='size-[0.9vw]'
                                  />
                                )}
                                <span>{b.text}</span>
                              </div>
                            )}
                            {b.type === 'PHONE_NUMBER' && (
                              <div className={cn(btnStyle, customBtnStyle)}>
                                {btnIcon && (
                                  <img
                                    src={ICONS?.call}
                                    alt='shareBtn'
                                    className='size-[0.9vw]'
                                  />
                                )}
                                <span>{b.text}</span>
                              </div>
                            )}

                            {b.type === 'COPY_CODE' && (
                              <div className={cn(btnStyle, customBtnStyle)}>
                                {btnIcon && (
                                  <img
                                    src={ICONS?.CopyLeft}
                                    alt='shareBtn'
                                    className='size-[0.9vw]'
                                  />
                                )}
                                <span>Copy Offer Code</span>
                              </div>
                            )}

                            {b.type === 'FLOW' && (
                              <div className={cn(btnStyle)}>
                                {b?.icon == 'DEFAULT' ? (
                                  <DocSvg height="14" width="14" color="#007aff" />
                                ) : b?.icon == 'DOCUMENT' ? (
                                  <DocumentIcon height="14" width="14" color="#007aff" />
                                ) : b?.icon == 'PROMOTION' ? (
                                  <Tag height="14" width="14" fill="#007aff" />
                                ) : b?.icon == 'REVIEW' ? (
                                  <ReviewIcon height="14" width="14" color="#007aff" />
                                ) : null}
                                <span>{b.text}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {allowedCustomButtonTypes?.includes(b?.type) && (
                          <button className={cn(btnStyle, customBtnStyle)}>
                            {btnIcon && (
                              <img
                                src={ICONS?.shareLink}
                                alt='shareBtn'
                                className='size-3.5'
                              />
                            )}
                            <span>{b.text}</span>
                          </button>
                        )}
                      </div>
                      {allowToShowEdge &&
                        allowedCustomButtonTypes?.includes(b?.type) && (
                          <div className='w-auto h-[1.875rem] absolute top-2 right-1'>
                            <EdgeHandler
                              type='start'
                              onEdgeMouseDown={({ position, payload }) => {
                                if (!isConnected)
                                  onEdgeMouseDown({
                                    nodeId: node?.uniq_id,
                                    connection_point,
                                    position: position,
                                    payload,
                                  });
                              }}
                              isConnected={isConnected}
                              color={color}
                              allowToEditFlow={allowToEditFlow}
                            />
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {category === 'MARKETING' && categoryType === 'CAROUSEL' && (
          <div className='w-full min-h-full flex flex-col flex-1 gap-8'>
            {carousel?.cards?.map((c) => {
              return (
                <div className='w-full h-full flex-1'>
                  <CarouselCard
                    data={c}
                    carousel={carousel}
                    node={node}
                    allowToEditFlow={allowToEditFlow}
                    checkIsEdgeConnected={checkIsEdgeConnected}
                    allowToShowEdge={allowToShowEdge}
                    onEdgeMouseDown={({ connection_point, position }) => {
                      onEdgeMouseDown({
                        nodeId: node?.id,
                        connection_point,
                        position: position,
                      });
                    }}
                    onButtonClick={onButtonClick}
                  />
                </div>
              );
            })}
          </div>
        )}
        {/* <div className='flex items-center justify-end my-2.5 px-2.5 weight-medium'>
          <button className='text-[0.7rem]'>Next Step</button>
        </div> */}
        {renderNextStepDraggable()}
      </div>
    </div>
  );
};

export default Template;
